import { useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";
import { SCENES, resolveNext } from "./scenes";
import { audio } from "./audio";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;
const LOCAL_KEY = "darkrequiem_save_v2";
const META_KEY = "darkrequiem_meta_v2";
const AUTH_TOKEN_KEY = "darkrequiem_token";

const currentSlot = () => (localStorage.getItem(AUTH_TOKEN_KEY) ? "self" : "guest");

export const INITIAL_STATE = {
  scene_id: "start",
  sanity: 100,
  composure: 100,
  corruption: 0,
  vengeance: 0,
  inventory: [],
  journal: [],
  flags: {},
  act: 1,
  updated_at: 0,
};

export const INITIAL_META = {
  endings_seen: [],
  ng_plus: false,
  files_found: {},
};

const clamp = (v, lo = 0, hi = 100) => Math.max(lo, Math.min(hi, v));

function applyEffectPure(prev, effect) {
  const next = { ...prev };
  if (effect.sanity) next.sanity = clamp(prev.sanity + effect.sanity);
  if (effect.composure) next.composure = clamp(prev.composure + effect.composure);
  if (effect.corruption) next.corruption = clamp(prev.corruption + effect.corruption);
  if (effect.vengeance) next.vengeance = clamp(prev.vengeance + effect.vengeance, 0, 999);
  if (effect.addItem) next.inventory = [...prev.inventory, effect.addItem];
  if (effect.addItem2) next.inventory = [...next.inventory, effect.addItem2];
  if (effect.addItem3) next.inventory = [...next.inventory, effect.addItem3];
  if (effect.craft) next.inventory = [...next.inventory, effect.craft];
  if (effect.consume) next.inventory = next.inventory.filter((it) => !effect.consume.includes(it));
  if (effect.removeItem) next.inventory = next.inventory.filter((it) => it !== effect.removeItem);
  if (effect.addJournal) next.journal = [...prev.journal, effect.addJournal];
  if (effect.setFlag) next.flags = { ...prev.flags, ...effect.setFlag };
  if (effect.act) next.act = effect.act;
  return next;
}

export function useGame() {
  const [state, setState] = useState(INITIAL_STATE);
  const [meta, setMeta] = useState(INITIAL_META);
  const [loaded, setLoaded] = useState(false);
  const [whisper, setWhisper] = useState(null);
  const stateRef = useRef(state);
  stateRef.current = state;

  // hydrate from localStorage first (synchronous, no flicker), then reconcile with backend if newer
  useEffect(() => {
    let localState = null;
    const raw = localStorage.getItem(LOCAL_KEY);
    if (raw) {
      try { localState = { ...INITIAL_STATE, ...JSON.parse(raw) }; setState(localState); }
      catch (e) { console.warn("Save parse failed", e); }
    }
    const rawMeta = localStorage.getItem(META_KEY);
    if (rawMeta) {
      try { setMeta({ ...INITIAL_META, ...JSON.parse(rawMeta) }); }
      catch (e) { console.warn("Meta parse failed", e); }
    }
    // Mark loaded immediately so UI (CONTINUE button) doesn't flicker.
    setLoaded(true);
    // Reconcile with backend only if server has a NEWER updated_at.
    axios.get(`${API}/load/${currentSlot()}`).then((r) => {
      if (r.data) {
        const serverUpdated = new Date(r.data.updated_at || 0).getTime();
        const localUpdated = localState?.updated_at ? new Date(localState.updated_at).getTime() : 0;
        if (!localState || serverUpdated > localUpdated) {
          setState({ ...INITIAL_STATE, ...r.data });
        }
      }
    }).catch(() => {});
  }, []);

  const persist = useCallback((next) => {
    const stamped = { ...next, updated_at: new Date().toISOString() };
    localStorage.setItem(LOCAL_KEY, JSON.stringify(stamped));
    axios.post(`${API}/save`, {
      slot: currentSlot(),
      scene_id: stamped.scene_id,
      sanity: stamped.sanity,
      composure: stamped.composure,
      corruption: stamped.corruption,
      vengeance: stamped.vengeance,
      inventory: stamped.inventory,
      journal: stamped.journal,
      flags: stamped.flags,
      act: stamped.act,
    }).catch(() => {});
    return stamped;
  }, []);

  const persistMeta = useCallback((m) => {
    localStorage.setItem(META_KEY, JSON.stringify(m));
  }, []);

  const triggerHallucination = useCallback(async (trigger) => {
    setWhisper({ text: "", trigger, loading: true });
    audio.whisper();
    try {
      const cur = stateRef.current;
      const res = await fetch(`${API}/hallucinate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sanity: cur.sanity, act: cur.act, trigger }),
      });
      if (!res.body) return;
      const reader = res.body.getReader();
      const decoder = new TextDecoder();
      let acc = "";
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        const chunk = decoder.decode(value);
        for (const line of chunk.split("\n")) {
          if (!line.startsWith("data: ")) continue;
          const payload = line.slice(6);
          if (payload === "[DONE]") continue;
          if (payload.startsWith("[ERROR]")) continue;
          acc += payload;
          setWhisper({ text: acc, trigger, loading: false });
        }
      }
    } catch (e) {
      setWhisper(null);
    }
  }, []);

  const choose = useCallback((choice) => {
    const prev = stateRef.current;
    const scene = SCENES[prev.scene_id];
    if (!scene) return;

    // Apply effects + resolve next scene atomically
    const withEffect = choice.effect ? applyEffectPure(prev, choice.effect) : prev;
    const nextIdRaw = resolveNext(prev.scene_id, choice, {
      ...withEffect,
      flags: { ...withEffect.flags, ngPlusUnlocked: meta.ng_plus },
    });

    // Handle collectible bookkeeping in meta
    if (choice.effect?.collectible && choice.effect.setFlag) {
      const newMeta = { ...meta, files_found: { ...meta.files_found, ...choice.effect.setFlag } };
      setMeta(newMeta);
      persistMeta(newMeta);
    }

    // Audio feedback: click every choice; buildup/jumpscare on scene mode transitions
    audio.click();
    // Ensure ambient is playing (in case user reloaded mid-game and hasn't opened audio)
    if (!audio.ambient) audio.startAmbient(SCENES[prev.scene_id]?.mode || "exploration");
    const nextScene = SCENES[nextIdRaw];
    const prevScene = SCENES[prev.scene_id];
    const nextMode = nextScene?.mode;
    const prevMode = prevScene?.mode;
    if (nextMode === "danger" && prevMode !== "danger") {
      // Jumpscare + buildup on danger entry
      audio.buildup();
      setTimeout(() => audio.jumpscare(), 900);
    } else if (nextMode === "stealth" && prevMode !== "stealth" && prevMode !== "danger") {
      // Softer buildup when entering stealth from exploration
      audio.buildup();
    }

    // Handle restart
    if (nextIdRaw === "__restart__") {
      const endingChoice = prev.flags?.endingChoice;
      if (endingChoice) {
        const newMeta = { ...meta, ng_plus: true };
        if (!newMeta.endings_seen.includes(endingChoice)) newMeta.endings_seen = [...newMeta.endings_seen, endingChoice];
        setMeta(newMeta);
        persistMeta(newMeta);
      }
      localStorage.removeItem(LOCAL_KEY);
      axios.delete(`${API}/save/${currentSlot()}`).catch(() => {});
      setState(INITIAL_STATE);
      persist(INITIAL_STATE);
      return;
    }

    // Record ending in meta if we're entering one
    if (nextIdRaw.startsWith("ending_")) {
      const key = nextIdRaw.replace("ending_", "");
      const newMeta = { ...meta, ng_plus: true };
      if (!newMeta.endings_seen.includes(key)) newMeta.endings_seen = [...newMeta.endings_seen, key];
      setMeta(newMeta);
      persistMeta(newMeta);
    }

    // Single atomic state update + single persist
    const finalState = { ...withEffect, scene_id: nextIdRaw };
    const stamped = persist(finalState);
    setState(stamped);

    if (choice.effect?.hallucinate) triggerHallucination(choice.effect.hallucinate);
  }, [meta, persist, persistMeta, triggerHallucination]);

  const dismissWhisper = useCallback(() => setWhisper(null), []);

  const resetGame = useCallback(() => {
    localStorage.removeItem(LOCAL_KEY);
    axios.delete(`${API}/save/default`).catch(() => {});
    setState(INITIAL_STATE);
  }, []);

  const playMemory = useCallback((key, memory) => {
    const prev = stateRef.current;
    if (prev.flags?.[`played_${key}`]) return; // already played — no farming
    const next = {
      ...prev,
      composure: clamp(prev.composure + (memory.composure_reward || 10)),
      journal: [...prev.journal, memory.journal],
      flags: { ...prev.flags, [`played_${key}`]: true },
    };
    const stamped = persist(next);
    setState(stamped);
  }, [persist]);

  return { state, meta, loaded, choose, whisper, dismissWhisper, resetGame, triggerHallucination, playMemory };
}
